// ─── Error Codes ────────────────────────────────────────────────

export enum ErrorCode {
  // Validation
  VALIDATION_FAILED = 'VALIDATION_FAILED',

  // Network
  NETWORK_ERROR = 'NETWORK_ERROR',

  // Auth
  AUTH_FAILED = 'AUTH_FAILED',
  FORBIDDEN = 'FORBIDDEN',
  SESSION_EXPIRED = 'SESSION_EXPIRED',

  // Resources
  NOT_FOUND = 'NOT_FOUND',
  CONFLICT = 'CONFLICT',

  // Rate limiting
  RATE_LIMIT = 'RATE_LIMIT',

  // Server
  SERVER_ERROR = 'SERVER_ERROR',

  // Domain-specific
  AGENT_CONFIG_FETCH_FAILED = 'AGENT_CONFIG_FETCH_FAILED',
  AGENT_CONFIG_UPDATE_FAILED = 'AGENT_CONFIG_UPDATE_FAILED',
  TEMPLATE_CREATE_FAILED = 'TEMPLATE_CREATE_FAILED',
  TEMPLATE_UPDATE_FAILED = 'TEMPLATE_UPDATE_FAILED',
  TEMPLATE_DELETE_FAILED = 'TEMPLATE_DELETE_FAILED',
  TEMPLATE_ACTIVATE_FAILED = 'TEMPLATE_ACTIVATE_FAILED',
  TEMPLATE_DEACTIVATE_FAILED = 'TEMPLATE_DEACTIVATE_FAILED',
  WHATSAPP_CONNECTION_FAILED = 'WHATSAPP_CONNECTION_FAILED',
  WHATSAPP_SESSION_FAILED = 'WHATSAPP_SESSION_FAILED',
  CAMPAIGN_CREATE_FAILED = 'CAMPAIGN_CREATE_FAILED',
  CAMPAIGN_SEND_FAILED = 'CAMPAIGN_SEND_FAILED',
  CAMPAIGN_ACTION_FAILED = 'CAMPAIGN_ACTION_FAILED',
  CONTACT_CREATE_FAILED = 'CONTACT_CREATE_FAILED',
  CONTACT_UPDATE_FAILED = 'CONTACT_UPDATE_FAILED',
  CONTACT_DELETE_FAILED = 'CONTACT_DELETE_FAILED',
  CONTACT_IMPORT_FAILED = 'CONTACT_IMPORT_FAILED',
  TAG_CREATE_FAILED = 'TAG_CREATE_FAILED',
  TAG_UPDATE_FAILED = 'TAG_UPDATE_FAILED',
  TAG_DELETE_FAILED = 'TAG_DELETE_FAILED',
  TEAM_MEMBER_ADD_FAILED = 'TEAM_MEMBER_ADD_FAILED',
  TEAM_MEMBER_UPDATE_FAILED = 'TEAM_MEMBER_UPDATE_FAILED',
  TEAM_MEMBER_REMOVE_FAILED = 'TEAM_MEMBER_REMOVE_FAILED',
  API_KEY_CREATE_FAILED = 'API_KEY_CREATE_FAILED',
  API_KEY_REVOKE_FAILED = 'API_KEY_REVOKE_FAILED',
  API_KEY_ROTATE_FAILED = 'API_KEY_ROTATE_FAILED',
  KNOWLEDGE_UPLOAD_FAILED = 'KNOWLEDGE_UPLOAD_FAILED',
  KNOWLEDGE_DELETE_FAILED = 'KNOWLEDGE_DELETE_FAILED',
  BILLING_CHECKOUT_FAILED = 'BILLING_CHECKOUT_FAILED',
  CONVERSATION_TAKEOVER_FAILED = 'CONVERSATION_TAKEOVER_FAILED',
  CONVERSATION_RELEASE_FAILED = 'CONVERSATION_RELEASE_FAILED',
  CONVERSATION_SEND_FAILED = 'CONVERSATION_SEND_FAILED',
  OPERATING_HOURS_UPDATE_FAILED = 'OPERATING_HOURS_UPDATE_FAILED',
  PROFILE_UPDATE_FAILED = 'PROFILE_UPDATE_FAILED',
  OTP_SEND_FAILED = 'OTP_SEND_FAILED',
  OTP_VERIFY_FAILED = 'OTP_VERIFY_FAILED',
  SIGNUP_FAILED = 'SIGNUP_FAILED',

  // Generic
  UNKNOWN = 'UNKNOWN',
}

// ─── Error Messages ─────────────────────────────────────────────

export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  [ErrorCode.VALIDATION_FAILED]: 'Invalid data. Please check your input.',
  [ErrorCode.NETWORK_ERROR]: 'Network error. Please check your connection and try again.',
  [ErrorCode.AUTH_FAILED]: 'Authentication failed. Please log in again.',
  [ErrorCode.FORBIDDEN]: 'You do not have permission to perform this action.',
  [ErrorCode.SESSION_EXPIRED]: 'Your session has expired. Please log in again.',
  [ErrorCode.NOT_FOUND]: 'The requested resource was not found.',
  [ErrorCode.CONFLICT]: 'A conflict occurred. Please refresh and try again.',
  [ErrorCode.RATE_LIMIT]: 'Too many requests. Please try again later.',
  [ErrorCode.SERVER_ERROR]: 'Server error. Please try again later.',
  [ErrorCode.AGENT_CONFIG_FETCH_FAILED]: 'Failed to load agent configuration.',
  [ErrorCode.AGENT_CONFIG_UPDATE_FAILED]: 'Failed to update agent configuration.',
  [ErrorCode.TEMPLATE_CREATE_FAILED]: 'Failed to create template.',
  [ErrorCode.TEMPLATE_UPDATE_FAILED]: 'Failed to update template.',
  [ErrorCode.TEMPLATE_DELETE_FAILED]: 'Failed to delete template.',
  [ErrorCode.TEMPLATE_ACTIVATE_FAILED]: 'Failed to activate template.',
  [ErrorCode.TEMPLATE_DEACTIVATE_FAILED]: 'Failed to deactivate template.',
  [ErrorCode.WHATSAPP_CONNECTION_FAILED]: 'Failed to connect to WhatsApp.',
  [ErrorCode.WHATSAPP_SESSION_FAILED]: 'Failed to manage WhatsApp session.',
  [ErrorCode.CAMPAIGN_CREATE_FAILED]: 'Failed to create campaign.',
  [ErrorCode.CAMPAIGN_SEND_FAILED]: 'Failed to send campaign.',
  [ErrorCode.CAMPAIGN_ACTION_FAILED]: 'Failed to perform campaign action.',
  [ErrorCode.CONTACT_CREATE_FAILED]: 'Failed to create contact.',
  [ErrorCode.CONTACT_UPDATE_FAILED]: 'Failed to update contact.',
  [ErrorCode.CONTACT_DELETE_FAILED]: 'Failed to delete contact.',
  [ErrorCode.CONTACT_IMPORT_FAILED]: 'Failed to import contacts.',
  [ErrorCode.TAG_CREATE_FAILED]: 'Failed to create tag.',
  [ErrorCode.TAG_UPDATE_FAILED]: 'Failed to update tag.',
  [ErrorCode.TAG_DELETE_FAILED]: 'Failed to delete tag.',
  [ErrorCode.TEAM_MEMBER_ADD_FAILED]: 'Failed to add team member.',
  [ErrorCode.TEAM_MEMBER_UPDATE_FAILED]: 'Failed to update team member.',
  [ErrorCode.TEAM_MEMBER_REMOVE_FAILED]: 'Failed to remove team member.',
  [ErrorCode.API_KEY_CREATE_FAILED]: 'Failed to create API key.',
  [ErrorCode.API_KEY_REVOKE_FAILED]: 'Failed to revoke API key.',
  [ErrorCode.API_KEY_ROTATE_FAILED]: 'Failed to rotate API key.',
  [ErrorCode.KNOWLEDGE_UPLOAD_FAILED]: 'Failed to upload document.',
  [ErrorCode.KNOWLEDGE_DELETE_FAILED]: 'Failed to delete document.',
  [ErrorCode.BILLING_CHECKOUT_FAILED]: 'Failed to process checkout.',
  [ErrorCode.CONVERSATION_TAKEOVER_FAILED]: 'Failed to take over conversation.',
  [ErrorCode.CONVERSATION_RELEASE_FAILED]: 'Failed to release conversation.',
  [ErrorCode.CONVERSATION_SEND_FAILED]: 'Failed to send message.',
  [ErrorCode.OPERATING_HOURS_UPDATE_FAILED]: 'Failed to update operating hours.',
  [ErrorCode.PROFILE_UPDATE_FAILED]: 'Failed to update profile.',
  [ErrorCode.OTP_SEND_FAILED]: 'Failed to send verification code.',
  [ErrorCode.OTP_VERIFY_FAILED]: 'Invalid verification code. Please try again.',
  [ErrorCode.SIGNUP_FAILED]: 'Failed to create account. Please try again.',
  [ErrorCode.UNKNOWN]: 'An unexpected error occurred.',
} as const;

// ─── Success Messages ───────────────────────────────────────────

export const SUCCESS_MESSAGES = {
  AGENT_CONFIG_UPDATED: 'Agent configuration updated successfully.',
  TEMPLATE_CREATED: 'Template created successfully.',
  TEMPLATE_UPDATED: 'Template updated successfully.',
  TEMPLATE_DELETED: 'Template deleted successfully.',
  TEMPLATE_ACTIVATED: 'Template activated.',
  TEMPLATE_DEACTIVATED: 'Template deactivated.',
  CAMPAIGN_CREATED: 'Campaign created successfully.',
  CAMPAIGN_SENT: 'Campaign sent!',
  CAMPAIGN_PAUSED: 'Campaign paused.',
  CAMPAIGN_RESUMED: 'Campaign resumed.',
  CAMPAIGN_CANCELLED: 'Campaign cancelled.',
  CAMPAIGN_DELETED: 'Campaign deleted.',
  CONTACT_CREATED: 'Contact created successfully.',
  CONTACT_UPDATED: 'Contact updated successfully.',
  CONTACT_DELETED: 'Contact deleted.',
  CONTACT_IMPORTED: 'Contacts imported successfully.',
  CONTACT_OPT_OUT: 'Contact opted out.',
  TAG_CREATED: 'Tag created successfully.',
  TAG_UPDATED: 'Tag updated.',
  TAG_DELETED: 'Tag deleted.',
  TEAM_MEMBER_ADDED: 'Team member added successfully.',
  TEAM_MEMBER_UPDATED: 'Team member updated.',
  TEAM_MEMBER_REMOVED: 'Team member removed.',
  API_KEY_CREATED: 'API key created. Copy it now — it won\'t be shown again.',
  API_KEY_REVOKED: 'API key revoked.',
  API_KEY_ROTATED: 'API key rotated. Copy the new key.',
  KNOWLEDGE_UPLOADED: 'Document uploaded successfully.',
  KNOWLEDGE_DELETED: 'Document deleted.',
  WHATSAPP_CONNECTED: 'WhatsApp connected successfully!',
  WHATSAPP_SESSION_CREATED: 'WhatsApp session created.',
  WHATSAPP_DISCONNECTED: 'WhatsApp disconnected.',
  OPERATING_HOURS_UPDATED: 'Operating hours updated.',
  PROFILE_UPDATED: 'Profile updated successfully.',
  OTP_SENT: 'Verification code sent to your email.',
  SIGNUP_COMPLETE: 'Account created! Setting up your workspace...',
  CONVERSATION_TAKEOVER: 'You are now handling this conversation.',
  CONVERSATION_RELEASED: 'Conversation returned to AI.',
  BILLING_CHECKOUT_STARTED: 'Redirecting to payment...',
} as const;
